import React, { useState, useEffect } from 'react';
import '../../styles/components/KeywordsList.scss'
import { getAppKeywords, createAppKeyword, updateAppKeyword, deleteAppKeywordAll } from '../../api/appKeyWordsApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { useDomain } from '../../context/DomainContext.js';
import { Box, Grid, Card, CardContent, Typography, Button, Fab } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';

import {
  Add as AddIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import KeywordModal from './KeyWordsModal.js';
import DeleteModal from '../DeleteModal.js';
import KeywordsDataGrid from './KeyWordsDataGrid.js';
import { useAuth } from '../../context/AuthContext.js';

function KeyWordsList() {
  const [keywords, setKeywords] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const { user } = useAuth();

  const loadKeywords = () => {
    showLoader();
    getAppKeywords()
      .then(response => {
        setKeywords(response.data)
      })
      .finally((data) => { hideLoader() })
      .catch(error => console.error('Error fetching keywords:', error));
  };

  const handleOpenDeleteModal = (keyword) => {
    setSelectedKeyword(keyword);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedKeyword(null);
  };

  const handleOpenModal = (keyword) => {
    setSelectedKeyword(keyword);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedKeyword(null);
  };

  const handleSaveKeyword = async (data, isNew) => {
    try {
      showLoader();
      let keywordMessage = isNew ? 'La palabra clave se creó correctamente' : 'La palabra clave se guardó correctamente';
      isNew ? await createAppKeyword(data) : await updateAppKeyword(selectedKeyword.id, data);

      setOpenModal(false);
      setSelectedKeyword(null);
      setOpenSnackbar({ open: true, text: keywordMessage, error: false });
      loadKeywords();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteKeyword = async (id) => {
    try {
      showLoader();
      await deleteAppKeywordAll(id);
      setOpenDeleteModal(false);
      setSelectedKeyword(null);
      setOpenSnackbar({ open: true, text: 'La palabra clave se eliminó correctamente', error: false });
      loadKeywords();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar la palabra clave', error: true });
    } finally {
      hideLoader();
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  useEffect(() => {
    if (selectedDomain) {
      loadKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon'>
                <KeyIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Total
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {keywords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon active-keywords'>
                <KeyIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Keywords requeridos
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {Array.isArray(keywords) ? keywords?.filter(u => u.is_required).length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {user?.isAdmin && <div className="add-button-container">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal({})}>Crear palabra clave</Button>
      </div>}

      <KeywordsDataGrid
        keywords={keywords}
        handleOpenDeleteModal={handleOpenDeleteModal}
        handleOpenModal={handleOpenModal}
      />


      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedKeyword}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteKeyword}
        message={'Esta seguro de eliminar palabra clave: ' + selectedKeyword?.name}
      />

      <KeywordModal
        openModal={openModal}
        selectedKeyword={selectedKeyword}
        handleCloseModal={handleCloseModal}
        handleSaveKeyword={handleSaveKeyword}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default KeyWordsList;
